import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as api from '../api/api';
import { buildSummaryParams } from '../utils/insights/paramBuilders';

/**
 * Hook para gestionar la verificación y generación de resúmenes
 * @param {string} summaryName - Nombre del resumen
 * @param {Object} dateRange - Rango de fechas
 * @param {string[]} selectedJobNames - JobNames seleccionados (persistidos cross-page)
 * @param {number} totalSelectedCount - Total de items seleccionados cross-page
 * @param {Array} tableItems - Items de la tabla (para validación)
 * @param {string|null} selectedSupervisor - Supervisor seleccionado
 * @param {Array} selectedAgents - Agentes seleccionados
 */
export function useSummaryGeneration(
  summaryName,
  dateRange,
  selectedJobNames,
  totalSelectedCount,
  tableItems,
  selectedSupervisor,
  selectedAgents
) {
  const history = useHistory();
  const { t } = useTranslation();
  
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [processParams, setProcessParams] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showPostSuccessModal, setShowPostSuccessModal] = useState(false);
  const [postRequestResult, setPostRequestResult] = useState("");

  const isCreateSummaryButtonEnabled = () => {
    if (!summaryName || summaryName.trim() === "") return false;
    
    const hasDateRange = dateRange !== null && dateRange.startDate && dateRange.endDate;
    const hasSelection = totalSelectedCount > 0;
    const hasTableItems = tableItems.length > 0;
    
    return hasDateRange && hasSelection && hasTableItems && !generatingSummary && !verifying;
  };

  const generateSummary = async () => {
    if (!summaryName || summaryName.trim() === "") return;
    if (!dateRange || totalSelectedCount === 0) return;

    try {
      setVerifying(true);
      setGeneratingSummary(true);
      
      const baseParams = {};
      const filteredJobNames = selectedJobNames.filter(Boolean);
      
      if (dateRange) {
        baseParams.startDate = dateRange.startDate;
        baseParams.endDate = dateRange.endDate;
      }
      
      if (filteredJobNames.length > 0) {
        baseParams.selectedJobNames = filteredJobNames;
      } else if (!dateRange) {
        return;
      }
      
      const verifyParams = buildSummaryParams(baseParams, {
        summaryName,
        selectedJobNames: filteredJobNames,
        supervisor: selectedSupervisor,
        agents: selectedAgents,
        includeSelectedJobNames: true
      });
      
      console.log('[useSummaryGeneration] Parámetros enviados a verifyData (GET):', verifyParams);
      const verifyResponse = await api.verifyData(verifyParams);
      
      if (!verifyResponse.found) {
        setPostRequestResult(verifyResponse.message || "No se encontraron datos que coincidan con los filtros");
        setShowPostSuccessModal(true);
        return;
      }
      
      setVerificationResult(verifyResponse);
      
      const params = buildSummaryParams(baseParams, {
        summaryName,
        selectedJobNames: filteredJobNames,
        supervisor: selectedSupervisor,
        agents: selectedAgents,
        includeSelectedJobNames: true
      });
      setProcessParams(params);
      
      setShowConfirmationModal(true);
      
    } catch (error) {
      console.error('Error en verificación:', error);
      setPostRequestResult(error.message || "Error al verificar datos");
      setShowPostSuccessModal(true);
    } finally {
      setVerifying(false);
      setGeneratingSummary(false);
    }
  };

  const processAfterConfirmation = async () => {
    try {
      setGeneratingSummary(true);
      
      console.log('[useSummaryGeneration] Parámetros enviados a postSummary (POST):', processParams);
      const response = await api.postSummary(processParams);
      
      // Mostrar resultado en modal si hay un mensaje específico
      if (response && response.message) {
        setPostRequestResult(response.message);
        setShowPostSuccessModal(true);
      }
      
      // Para análisis globales, no actualizamos la tabla de elementos individuales
      // El resumen se guarda automáticamente en el historial por el backend

      // Navegar a la pantalla de historial después de generar
      setTimeout(() => {
        history.push('/history-insights-calls');
      }, 1000);
      
    } catch (error) {
      console.error('Error generating summary:', error);
      // Mostrar error en modal
      setPostRequestResult(error.message || t("insights.postError"));
      setShowPostSuccessModal(true);
    } finally {
      setGeneratingSummary(false);
      setShowConfirmationModal(false);
    }
  };

  return {
    generatingSummary,
    verifying,
    verificationResult,
    processParams,
    generateSummary,
    processAfterConfirmation,
    isCreateSummaryButtonEnabled,
    showConfirmationModal,
    setShowConfirmationModal,
    showPostSuccessModal,
    setShowPostSuccessModal,
    postRequestResult,
    setPostRequestResult
  };
}

